// How a funding window extension reads on the property screen: the line the
// status banner and the funding window both carry, and whether the seller has a
// decision to make. Pure — every state is a unit test.
import { formatDayMonthYear } from '@shared/utils'
import type { WindowExtensionProposal } from '../types/propertyTypes'

// The seller's signature statuses that still owe an answer.
const AWAITING_STATUSES: ReadonlySet<string> = new Set(['pending', 'sent', 'viewed'])

export const EXTENSION_DECLINE_REASON_MIN_LENGTH = 10
export const EXTENSION_DECLINE_REASON_MAX_LENGTH = 500

/** Whether the seller has a decision to make: their own line, unanswered, nobody declined. */
export function isExtensionActionNeeded(proposal: WindowExtensionProposal | null): boolean {
  if (!proposal?.ownSignatoryId || proposal.declinedAt) return false
  return AWAITING_STATUSES.has(proposal.ownSignatureStatus ?? '')
}

function daysLabel(days: number): string {
  return `${days} day${days === 1 ? '' : 's'}`
}

/**
 * The one line about the extension, or null when there is none. Shown in the
 * status banner and under the funding window (FSD 3.3.2 item 8).
 */
export function extensionNoticeOf(proposal: WindowExtensionProposal | null): string | null {
  if (!proposal) return null
  const days = daysLabel(proposal.extensionDays)

  if (proposal.declinedAt) {
    return 'The funding window extension was declined. Our team will be in touch about what happens next.'
  }
  if (isExtensionActionNeeded(proposal)) {
    const by = proposal.expiresAt ? ` by ${formatDayMonthYear(proposal.expiresAt)}` : ''
    return `Sliceal has proposed extending your funding window by ${days}. Review and respond${by}.`
  }
  if (proposal.ownSignatoryId) {
    return `You accepted the ${proposal.extensionDays}-day extension. ${proposal.ownersAccepted} of ${proposal.ownersTotal} owners have accepted so far.`
  }
  return `Sliceal has proposed extending the funding window by ${days}. The owners are reviewing it.`
}

/** Whether a decline reason is within the FSD's 10–500 characters. */
export function declineReasonErrorOf(reason: string): string {
  const length = reason.trim().length
  if (length < EXTENSION_DECLINE_REASON_MIN_LENGTH) {
    return `Tell us why — at least ${EXTENSION_DECLINE_REASON_MIN_LENGTH} characters.`
  }
  if (length > EXTENSION_DECLINE_REASON_MAX_LENGTH) {
    return `Keep it under ${EXTENSION_DECLINE_REASON_MAX_LENGTH} characters.`
  }
  return ''
}
